import { useState } from "react";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, publicShopUrl } from "../../lib/api";
import { usePolling } from "../../hooks/usePolling";
import { PageHeader } from "../../components/PageHeader";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

export default function AdminMembers() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  usePolling(async () => {
    const { data } = await api.get("/admin/members");
    setItems(data);
  }, 5000);

  const toggle = async (m, v) => {
    setItems((l) => l.map((x) => (x.user_id === m.user_id ? { ...x, is_active: v } : x)));
    try {
      await api.put(`/admin/members/${m.user_id}/status`, { is_active: v });
      toast.success(`${m.name} ${v ? "diaktifkan" : "dinonaktifkan"}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const filtered = (items || []).filter((m) => `${m.name} ${m.email} ${m.shop_name || ""}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader title="Daftar Member" subtitle="Aktifkan atau nonaktifkan akun pemilik toko." />
      <div className="rounded-2xl border border-slate-200 bg-white fade-up">
        <div className="p-4 border-b border-slate-100">
          <div className="relative max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari member..." className="pl-9" data-testid="member-search-input" />
          </div>
        </div>
        <div className="md:hidden divide-y divide-slate-100" data-testid="members-mobile-list">
          {filtered.map((m) => (
            <div key={m.user_id} className="p-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-sm">{m.name}</p>
                <p className="truncate text-xs text-slate-500">{m.email} · {m.login_method}</p>
                <p className="text-xs text-slate-500">{m.shop_slug ? `/${m.shop_slug}` : "Belum buat toko"}{m.whatsapp ? ` · ${m.whatsapp}` : ""}</p>
              </div>
              <div className="flex flex-col items-end gap-1.5">
                <Switch checked={!!m.is_active} onCheckedChange={(v) => toggle(m, v)} data-testid={`m-toggle-member-status-${m.user_id}`} />
                <span className={`text-[10px] font-semibold ${m.is_active ? "text-emerald-600" : "text-red-600"}`}>{m.is_active ? "Aktif" : "Nonaktif"}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="hidden md:block overflow-x-auto">
          <Table data-testid="members-table">
            <TableHeader><TableRow>
              <TableHead>Nama</TableHead><TableHead>Email</TableHead><TableHead>WhatsApp</TableHead><TableHead>Toko</TableHead>
              <TableHead>Login</TableHead><TableHead>Bergabung</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aktif</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items && !filtered.length && <TableRow><TableCell colSpan={8} className="py-10 text-center text-sm text-slate-500">Belum ada member.</TableCell></TableRow>}
              {filtered.map((m) => (
                <TableRow key={m.user_id} data-testid={`member-row-${m.user_id}`}>
                  <TableCell className="font-semibold">{m.name}</TableCell>
                  <TableCell className="text-sm">{m.email}</TableCell>
                  <TableCell className="text-sm">{m.whatsapp || "-"}</TableCell>
                  <TableCell>{m.shop_slug ? <a href={publicShopUrl(m.shop_slug)} target="_blank" rel="noreferrer" className="text-sm text-blue-600 hover:underline">{m.shop_name}</a> : <span className="text-xs text-slate-400">Belum buat toko</span>}</TableCell>
                  <TableCell className="text-xs">{m.login_method}</TableCell>
                  <TableCell className="text-xs whitespace-nowrap">{new Date(m.created_at).toLocaleDateString("id-ID")}</TableCell>
                  <TableCell>
                    <Badge className={m.is_active ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-50" : "bg-red-50 text-red-700 hover:bg-red-50"} data-testid={`member-status-${m.user_id}`}>
                      {m.is_active ? "Aktif" : "Nonaktif"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right"><Switch checked={!!m.is_active} onCheckedChange={(v) => toggle(m, v)} data-testid={`toggle-member-status-${m.user_id}`} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
